import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { BarChart3, Palette, MonitorSmartphone, ArrowRight } from "lucide-react";
import { waLink, DEFAULT_WA_MSG, IMAGES } from "@/config";
import { EASE } from "@/components/Reveal";
import CinematicButton from "@/components/CinematicButton";

const HIGHLIGHTS = [
    { icon: BarChart3, label: "Data Analytics" },
    { icon: Palette, label: "Creative & Digital" },
    { icon: MonitorSmartphone, label: "Website Design" },
];

const lineAnim = (delay) => ({
    initial: { y: "110%" },
    animate: { y: "0%" },
    transition: { duration: 0.95, delay, ease: EASE },
});

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 60]);

    return (
        <section id="home" className="relative overflow-hidden" data-testid="hero-section">
            {/* soft olive wash */}
            <div className="pointer-events-none absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-olive/20 blur-3xl" />
            <div className="pointer-events-none absolute top-64 -left-52 w-[460px] h-[460px] rounded-full bg-gold/25 blur-3xl" />

            <div className="relative mx-auto max-w-7xl px-5 sm:px-8 pt-32 pb-16 lg:pt-40 lg:pb-24 grid lg:grid-cols-12 gap-14 lg:gap-10 items-center">
                {/* Left */}
                <div className="lg:col-span-6">
                    <motion.div
                        initial={{ opacity: 0, x: -24 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
                        className="flex items-center gap-3 mb-7"
                    >
                        <span className="h-px w-10 bg-gold-deep" />
                        <span className="text-[11px] sm:text-xs font-semibold tracking-[0.34em] text-burgundy">
                            WELCOME TO ANALYTROVA
                        </span>
                    </motion.div>

                    <h1 className="text-[clamp(2.4rem,5vw,4.15rem)] leading-[1.06] text-charcoal">
                        <span className="block overflow-hidden pb-1">
                            <motion.span className="block" {...lineAnim(0.2)}>
                                Smart Solutions for
                            </motion.span>
                        </span>
                        <span className="block overflow-hidden pb-2">
                            <motion.span
                                className="block italic text-burgundy"
                                {...lineAnim(0.35)}
                            >
                                Growing Businesses
                            </motion.span>
                        </span>
                    </h1>

                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.6, ease: EASE }}
                        className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-charcoal/75"
                    >
                        We help businesses turn data into insights and build a
                        stronger digital presence through professional data
                        analytics, website design, branding and digital
                        services.
                    </motion.p>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
                        className="mt-9 flex flex-wrap items-center gap-4"
                    >
                        <CinematicButton href="#contact" className="btn btn-burgundy group" testid="hero-cta-talk">
                            Let's Talk
                            <ArrowRight className="btn-arrow w-4 h-4" />
                        </CinematicButton>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.9, delay: 0.95 }}
                        className="mt-12 grid grid-cols-3 gap-4 max-w-lg border-t border-burgundy/15 pt-7"
                    >
                        {HIGHLIGHTS.map(({ icon: Icon, label }) => (
                            <div key={label} className="flex flex-col gap-2">
                                <Icon className="w-5 h-5 text-olive-deep" strokeWidth={1.6} />
                                <span className="text-[13px] sm:text-sm font-medium text-charcoal/85 leading-snug">
                                    {label}
                                </span>
                            </div>
                        ))}
                    </motion.div>
                </div>

                {/* Right — arch-framed image with parallax */}
                <div ref={ref} className="lg:col-span-6 relative">
                    <motion.div
                        initial={{ opacity: 0, y: 40, scale: 0.97 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: 1.1, delay: 0.45, ease: EASE }}
                        className="relative mx-auto max-w-[520px]"
                    >
                        <div className="absolute -top-5 -right-5 w-full h-full arch border border-gold-deep/60" aria-hidden="true" />
                        <div className="arch overflow-hidden img-zoom shadow-[0_40px_80px_-40px_rgba(90,0,16,0.45)]">
                            <motion.img
                                style={{ y: imgY }}
                                src={IMAGES.hero}
                                alt="Professional analytics workspace with business dashboard"
                                className="w-full aspect-[4/4.6] object-cover scale-110"
                                data-testid="hero-image"
                            />
                        </div>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 1.15, ease: EASE }}
                            className="absolute -bottom-6 left-2 sm:-left-8 flex items-center gap-3 bg-cream/95 backdrop-blur border border-burgundy/12 rounded-full pl-2 pr-5 py-2 shadow-lg"
                            data-testid="hero-badge"
                        >
                            <span className="w-9 h-9 rounded-full bg-burgundy text-cream flex items-center justify-center">
                                <BarChart3 className="w-4 h-4" />
                            </span>
                            <span className="font-serif italic text-[15px] text-burgundy">
                                Where Data Meets Creativity
                            </span>
                        </motion.div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}
